import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Disclosure } from "@/components/design/Disclosure";
import { Section } from "@/components/design/Section";
import {
  fetchTalentDossier,
  fetchTitleDossier,
  TobisNotConfiguredError,
  type TobisDossierGroup,
} from "@/lib/tobis";
import { logApplicationFailure } from "@/lib/user-error";

// Alles, was der Bestand zu einem Titel oder einer Person hergibt, Relation fuer
// Relation. Werkzeug zum Entscheiden, was fachlich traegt - was sich bewaehrt,
// wandert danach in gestaltete Sektionen.
//
// Die Gruppen starten zugeklappt: die Ansicht antwortet damit zuerst "welche
// Quellen kennen diesen Titel und mit wie vielen Zeilen", und erst auf Klick
// "was steht drin". Aufgeklappt waeren es mehrere Hundert Zeilen am Stueck, in
// denen die Uebersicht verloren geht.

interface TobisDossierProps {
  kind: "title" | "talent";
  id: string;
}

/** Ein einzelner Satz liest sich als Feldliste, nicht als 25-spaltige Tabelle. */
function FieldList({ columns, values }: { columns: string[]; values: (string | null)[] }) {
  const intl = useIntl();
  const filled = columns.map((c, i) => [c, values[i] ?? null] as const).filter(([, v]) => v !== null);

  if (filled.length === 0) {
    return (
      <p className="field-meta text-ink/45">
        {intl.formatMessage({ id: "tobis.dossier.allEmpty", defaultMessage: "Every field is empty." })}
      </p>
    );
  }

  return (
    <>
      <dl className="grid grid-cols-1 gap-3 @sm:grid-cols-2 @lg:grid-cols-3">
        {filled.map(([column, value]) => (
          <div key={column}>
            <dt className="field-label text-ink/45">{column}</dt>
            <dd className="field-value break-words text-ink/80">{value}</dd>
          </div>
        ))}
      </dl>
      {filled.length < columns.length ? (
        <p className="mt-4 field-meta text-ink/45">
          {intl.formatMessage(
            {
              id: "tobis.dossier.emptyFields",
              defaultMessage: "{count} further fields are empty: {names}",
            },
            {
              count: columns.length - filled.length,
              names: columns.filter((_, i) => (values[i] ?? null) === null).join(", "),
            },
          )}
        </p>
      ) : null}
    </>
  );
}

/** Mehrere Saetze als Tabelle. Waagerecht scrollbar, weil manche Quellen 20 Spalten fuehren. */
function RowTable({ columns, rows }: { columns: string[]; rows: (string | null)[][] }) {
  return (
    <div className="max-h-96 overflow-auto">
      <table className="w-full min-w-max text-left text-[12px] tabular-nums">
        <thead className="sticky top-0 bg-page">
          <tr>
            {columns.map((c) => (
              <th key={c} className="whitespace-nowrap py-1.5 pr-4 font-normal text-ink/45">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-t border-ink/5">
              {row.map((value, j) => (
                <td key={j} className="whitespace-nowrap py-1.5 pr-4 text-ink/70">
                  {value ?? <span className="text-ink/30">–</span>}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function TobisDossier({ kind, id }: TobisDossierProps) {
  const intl = useIntl();
  const [groups, setGroups] = useState<TobisDossierGroup[] | null>(null);
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");

  const load = useCallback(async () => {
    try {
      setGroups(await (kind === "title" ? fetchTitleDossier(id) : fetchTalentDossier(id)));
      setPhase("ready");
    } catch (err) {
      if (!(err instanceof TobisNotConfiguredError)) logApplicationFailure("tobis.dossier");
      setPhase("error");
    }
  }, [kind, id]);

  useEffect(() => {
    void load();
  }, [load]);

  const caveatText = (caveat: TobisDossierGroup["caveat"]) => {
    if (caveat === "titleYear") {
      return intl.formatMessage({
        id: "tobis.dossier.caveatTitleYear",
        defaultMessage:
          "Linked by title and year (±1), not by ID - this source carries no IMDb id, so a wrong match is possible.",
      });
    }
    if (caveat === "titleWriter") {
      return intl.formatMessage({
        id: "tobis.dossier.caveatTitleWriter",
        defaultMessage:
          "Linked by title and, where possible, by year or screenwriter. The match_origin column says which carried: 'title+year' and 'title+writer' are corroborated, 'title' rests on the name alone.",
      });
    }
    return intl.formatMessage({
      id: "tobis.dossier.caveatName",
      defaultMessage: "Linked by plain name, not by ID - namesakes end up in the same row set.",
    });
  };

  return (
    <Section
      title={intl.formatMessage({ id: "tobis.dossier.title", defaultMessage: "Everything we hold" })}
      right={
        groups ? <span className="text-[12px] text-ink/40">{groups.length}</span> : null
      }
      empty={phase === "ready" && groups?.length === 0}
      emptyLabel={intl.formatMessage({
        id: "tobis.dossier.empty",
        defaultMessage: "No source holds anything on this entry.",
      })}
    >
      {phase === "loading" ? (
        <p className="field-meta text-ink/45">
          {intl.formatMessage({ id: "tobis.dossier.loading", defaultMessage: "Collecting sources …" })}
        </p>
      ) : null}

      {phase === "error" ? (
        <p className="field-meta text-ink/55">
          {intl.formatMessage({
            id: "tobis.dossier.loadError",
            defaultMessage: "The sources could not be read.",
          })}
        </p>
      ) : null}

      <div className="space-y-2">
        {groups?.map((g) => (
          <Disclosure
            key={g.key}
            title={g.label}
            count={
              g.failed
                ? intl.formatMessage({ id: "tobis.dossier.failed", defaultMessage: "not readable" })
                : g.rows.length === 1
                  ? intl.formatMessage(
                      { id: "tobis.dossier.fieldCount", defaultMessage: "{count} fields" },
                      { count: g.columns.length },
                    )
                  : intl.formatMessage(
                      { id: "tobis.dossier.rowCount", defaultMessage: "{count} rows" },
                      { count: g.rows.length },
                    )
            }
          >
            {g.caveat ? <p className="mb-4 field-meta text-ink/45">{caveatText(g.caveat)}</p> : null}
            {g.failed ? null : g.rows.length === 1 && g.rows[0] ? (
              <FieldList columns={g.columns} values={g.rows[0]} />
            ) : (
              <RowTable columns={g.columns} rows={g.rows} />
            )}
          </Disclosure>
        ))}
      </div>
    </Section>
  );
}
